/**
 * Bundle Editor
 *
 * Update flow for the bundle builder: hydrating the builder from a bundle that
 * is already in the cart, and the messaging that goes with it.
 *
 * This lives in its own file on purpose. The create flow must stay byte
 * identical to what it was, and keeping the update path out of the builder
 * class means "did this touch the create flow" is answerable from a one file
 * diff. Nothing in here runs unless the section rendered an editing context,
 * which Liquid only does when there is exactly one bundle in the cart and the
 * exact tier gate is in play.
 *
 * Covers reading the context, hydrating, the update button state, saving by
 * replacing the cart bundle, and removing it.
 */
window.BundleEditor = (function () {
  'use strict';

  /**
   * Read the editing context that sections/bundle-builder.liquid stamped on the
   * container. Returns null when there is nothing to edit, which is the signal
   * to leave the builder in its ordinary create behaviour.
   *
   * The decision is taken here, once, from markup that was rendered with the
   * page. It is never re-decided from a fetch, so there is no window in which
   * create-path code runs for a customer who is actually editing.
   */
  function readContext(container) {
    var bundleId = container.dataset.editingBundleId;
    if (!bundleId) return null;

    var lines;
    try {
      lines = JSON.parse(container.dataset.editingLines || '[]');
    } catch (e) {
      return null;
    }
    if (!Array.isArray(lines) || lines.length === 0) return null;

    // Consolidate by variant. The builder's model holds one entry per variant
    // carrying a quantity, while the cart can in principle spread the same
    // variant across more than one line.
    var byVariant = {};
    var order = [];

    lines.forEach(function (line) {
      var key = String(line.variantId);
      var qty = parseInt(line.quantity, 10) || 0;
      if (qty <= 0) return;

      if (byVariant[key]) {
        byVariant[key].quantity += qty;
        byVariant[key].keys.push(line.key);
      } else {
        byVariant[key] = {
          variantId: key,
          quantity: qty,
          title: line.title || '',
          keys: [line.key]
        };
        order.push(key);
      }
    });

    if (order.length === 0) return null;

    return {
      bundleId: bundleId,
      lines: order.map(function (k) { return byVariant[k]; })
    };
  }

  /**
   * Rebuild the builder's selection from the cart bundle.
   *
   * Returns { ok, missing, unavailable }.
   *
   *   ok false  the bundle could not be fully reconstructed, so the caller must
   *             fall back to the lock. A silently short edit session, where the
   *             customer sees five of their six items and no explanation, is a
   *             worse outcome than an honest wall.
   *   missing   titles we could not place, for that honest wall.
   *   unavailable  titles that hydrated but are sold out. These do NOT fail the
   *             hydration: the product is on the page, the customer can see it
   *             and swap it out, which is a problem they can actually solve.
   */
  function hydrate(builder, ctx) {
    var missing = [];
    var unavailable = [];
    var items = [];
    var total = 0;

    ctx.lines.forEach(function (line) {
      var card = builder.findCardByVariantId(line.variantId);
      if (!card) {
        missing.push(line.title || 'an item');
        return;
      }

      var variants;
      try {
        variants = JSON.parse(card.dataset.variants);
      } catch (e) {
        missing.push(line.title || 'an item');
        return;
      }

      var variant = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(line.variantId)) {
          variant = variants[i];
          break;
        }
      }
      if (!variant) {
        missing.push(line.title || 'an item');
        return;
      }

      var select = card.querySelector('.bundle-product-card__variant-select');
      if (select) select.value = variant.id;

      if (!variant.available) {
        unavailable.push(card.dataset.productTitle || line.title || 'an item');
      }

      items.push({
        id: builder.generateId(),
        productId: card.dataset.productId,
        productHandle: card.dataset.productHandle,
        variantId: String(variant.id),
        title: card.dataset.productTitle,
        variantTitle: variant.title !== 'Default Title' ? variant.title : null,
        price: variant.price,
        image: card.dataset.productImage,
        category: card.dataset.productCategory || null,
        available: variant.available,
        quantity: line.quantity
      });

      total += line.quantity;
    });

    if (missing.length > 0) {
      return { ok: false, missing: missing, unavailable: unavailable };
    }

    // A cart bundle should never exceed the builder's own ceiling. If it does,
    // something is out of step and we should not pretend we can represent it.
    if (total > builder.getMaxCap()) {
      return { ok: false, missing: [], unavailable: unavailable, overCap: true };
    }

    builder.state.selectedItems = items;
    return { ok: true, missing: [], unavailable: unavailable };
  }

  /**
   * Inject the editing banner above the build-your-own block, plus a compact
   * chip in the sidebar header.
   *
   * The banner goes at the top of the page rather than beside the button
   * because on mobile the sidebar is a collapsed bottom sheet that also hides
   * its thumbnail grid, so anything injected down there is easy to miss. The
   * chip covers the collapsed case, since the sidebar header stays visible.
   */
  function renderBanner(builder, result) {
    var anchor = builder.container.querySelector('.bundle-byo');
    if (!anchor || !anchor.parentNode) return;

    var banner = document.createElement('div');
    banner.className = 'bundle-edit-banner';
    banner.setAttribute('role', 'status');

    var html = '';
    html += '<span class="bundle-edit-banner__icon" aria-hidden="true">';
    html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ';
    html += 'stroke-linecap="round" stroke-linejoin="round" width="20" height="20">';
    html += '<path d="M12 20h9"></path>';
    html += '<path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"></path>';
    html += '</svg></span>';

    html += '<span class="bundle-edit-banner__body">';
    html += '<strong class="bundle-edit-banner__title">Editing the bundle in your cart</strong>';
    html += '<span class="bundle-edit-banner__note">';
    html += 'Change anything you like. Your cart keeps its current bundle until you save.';
    html += '</span>';

    if (result.unavailable.length > 0) {
      html += '<span class="bundle-edit-banner__warn">';
      html += escapeHtml(joinNames(result.unavailable));
      html += result.unavailable.length > 1 ? ' are sold out. ' : ' is sold out. ';
      html += 'Swap ' + (result.unavailable.length > 1 ? 'them' : 'it') + ' out before saving.';
      html += '</span>';
    }

    html += '</span>';
    banner.innerHTML = html;
    anchor.parentNode.insertBefore(banner, anchor);

    var header = document.querySelector('.bundle-sidebar__header');
    if (header && !header.querySelector('.bundle-sidebar__edit-chip')) {
      var chip = document.createElement('span');
      chip.className = 'bundle-sidebar__edit-chip';
      chip.textContent = 'Editing';
      header.insertBefore(chip, header.firstChild);
    }
  }

  /**
   * Replace the lock copy when we know why we locked. The generic "only one
   * bundle can be active at a time" is true but unhelpful to someone who came
   * here specifically to change the bundle we just failed to load.
   */
  /**
   * Short button label for a lock we can explain. Returning null keeps the
   * original wording, which is correct for the plain "one bundle at a time"
   * case. Every other lock now says which one it is on the button itself,
   * because "Your Llama already has this bundle!" is what gets reported and it
   * is the same sentence whatever went wrong.
   */
  function lockLabel(result, container) {
    if (container && parseInt(container.dataset.bundleCount, 10) > 1) {
      return 'Two bundles in your cart';
    }
    if (result && result.missing && result.missing.length > 0) {
      return 'This bundle cannot be edited here';
    }
    if (result && result.overCap) {
      return 'This bundle is too large to edit here';
    }
    return null;
  }

  function lockReason(result, container) {
    // Two or more bundles in one cart is only reachable if a save was
    // interrupted between its two writes. Nothing else detects it, and both
    // bundles price correctly, so the customer could check out with twelve
    // items and never notice.
    if (container && parseInt(container.dataset.bundleCount, 10) > 1) {
      return 'Your cart has more than one bundle, which should not happen. ' +
        'Remove one of them from your cart and we will pick up the other here.';
    }

    if (!result) return null;

    if (result.missing && result.missing.length > 0) {
      return 'We could not load your current bundle here because ' +
        joinNames(result.missing) +
        (result.missing.length > 1 ? ' are ' : ' is ') +
        'no longer available to bundle. To change your bundle, remove it from ' +
        'your cart and build a new one.';
    }

    if (result.overCap) {
      return 'Your current bundle is larger than this builder allows, so it ' +
        'cannot be edited here. To change it, remove it from your cart and ' +
        'build a new one.';
    }

    return null;
  }

  /**
   * A stable fingerprint of a selection, used only to answer "has anything
   * actually changed since we loaded this". Order independent, because moving
   * the same items around is not a change.
   */
  function signature(items) {
    return (items || [])
      .map(function (i) { return i.variantId + ':' + i.quantity; })
      .sort()
      .join('|');
  }

  function isChanged(builder) {
    return signature(builder.state.selectedItems) !== builder.editOriginalSignature;
  }

  /**
   * Button, price and secondary actions for the update flow.
   *
   * The primary button never changes role. It is always the save action, just
   * disabled until there is something to save. Making it navigate when nothing
   * has changed would mean the same pixel either leaves the page or writes to
   * the cart depending on invisible state, and on mobile it would make the
   * loudest thing on arrival an invitation to leave.
   */
  function applyButtonState(builder, totals) {
    var btn = builder.elements.addToCartBtn;
    var label = builder.elements.submitBtnText;
    var changed = isChanged(builder);
    var onTier = !builder.dynamicMode || totals.isComplete;
    var canSave = changed && onTier && totals.isValid && !builder.state.isAddingToCart;

    if (btn) {
      btn.disabled = !canSave;
      btn.classList.remove('bundle-sidebar__cta--locked');
      btn.classList.toggle('is-priced', canSave && totals.itemCount > 0);
    }

    if (label) {
      if (changed && !onTier) {
        // Identical rule and identical words to the create flow. Being off tier
        // costs the customer nothing here, because the cart still holds their
        // original bundle until they save.
        var remaining = Math.max(1, builder.state.currentBundleSize - totals.itemCount);
        label.textContent = totals.itemCount === 0
          ? 'Add ' + builder.state.currentBundleSize + ' items to start'
          : 'Add ' + remaining + ' more item' + (remaining === 1 ? '' : 's');
      } else {
        label.textContent = 'Update bundle';
      }
    }

    // Price keeps exactly the meaning it has in the create flow: struck subtotal
    // against the discounted total. Deliberately not reused to show the previous
    // bundle price. One element with two meanings would render a struck lower
    // number beside a higher one every time a customer drops a tier, which is a
    // savings pattern used to display a price increase.
    if (builder.elements.submitBtnPriceNow) {
      builder.elements.submitBtnPriceNow.textContent = builder.formatMoney(totals.oneTimeTotal);
    }
    if (builder.elements.submitBtnPriceWas) {
      if (totals.bundleDiscountAmount > 0) {
        builder.elements.submitBtnPriceWas.textContent = builder.formatMoney(totals.subtotal);
        builder.elements.submitBtnPriceWas.style.display = '';
      } else {
        builder.elements.submitBtnPriceWas.style.display = 'none';
      }
    }

    renderTierNote(builder, totals, changed && onTier);
    renderSecondaryActions(builder, changed);
  }

  /**
   * A tier change is money, so say it in words rather than leaving the customer
   * to infer it from two numbers on a button.
   */
  function renderTierNote(builder, totals, active) {
    var host = builder.elements.addToCartBtn && builder.elements.addToCartBtn.parentElement;
    if (!host) return;

    var note = host.querySelector('.bundle-sidebar__edit-note');
    var was = Math.round((builder.editOriginalRate || 0) * 100);
    var now = Math.round(totals.discountRate * 100);

    if (!active || was === now) {
      if (note) note.remove();
      return;
    }

    if (!note) {
      note = document.createElement('p');
      note.className = 'bundle-sidebar__edit-note';
      host.insertBefore(note, host.firstChild);
    }

    note.classList.toggle('bundle-sidebar__edit-note--down', now < was);
    note.textContent = was === 0
      ? 'This change unlocks ' + now + '% off.'
      : 'This change moves you from ' + was + '% off to ' + now + '% off.';
  }

  /**
   * Escape hatches, present in every state including off tier and zero items.
   * Without these the only signposted exits are destructive or silent.
   */
  function renderSecondaryActions(builder, changed) {
    var host = builder.elements.addToCartBtn && builder.elements.addToCartBtn.parentElement;
    if (!host) return;

    var row = host.querySelector('.bundle-sidebar__edit-actions');
    if (!row) {
      row = document.createElement('div');
      row.className = 'bundle-sidebar__edit-actions';
      row.innerHTML =
        '<a class="bundle-sidebar__edit-link" href="/cart" data-edit-cancel></a>' +
        '<button type="button" class="bundle-sidebar__edit-link bundle-sidebar__edit-link--danger" data-edit-remove>' +
        'Remove bundle from cart</button>';
      host.appendChild(row);

      // Two taps rather than one. Removing is destructive and irreversible, and
      // it sits next to a benign link at the same visual weight, so a stray tap
      // is easy. A second tap is cheaper than a modal and harder to do by
      // accident. The intent resets if they leave it alone.
      var removeBtn = row.querySelector('[data-edit-remove]');
      var armed = false;
      var disarm = null;

      removeBtn.addEventListener('click', function () {
        if (armed) {
          clearTimeout(disarm);
          removeBtn.textContent = 'Removing...';
          removeBtn.disabled = true;
          removeBundle(builder);
          return;
        }
        armed = true;
        removeBtn.textContent = 'Tap again to remove';
        removeBtn.classList.add('is-armed');
        disarm = setTimeout(function () {
          armed = false;
          removeBtn.textContent = 'Remove bundle from cart';
          removeBtn.classList.remove('is-armed');
        }, 4000);
      });
    }

    var cancel = row.querySelector('[data-edit-cancel]');
    if (cancel) {
      cancel.textContent = changed ? 'Cancel, keep my current bundle' : 'Back to cart';
    }
  }

  /**
   * Edit sessions otherwise fire bundle_builder_viewed and never
   * bundle_add_to_cart, which would show up as builder conversion falling for
   * reasons that have nothing to do with the builder.
   */
  function trackSession(builder) {
    builder.track('bundle_edit_started', { item_count: builder.getTotalQuantity() });

    window.addEventListener('pagehide', function () {
      if (builder.editSaved) return;
      if (!isChanged(builder)) return;
      builder.track('bundle_edit_abandoned', { item_count: builder.getTotalQuantity() });
    });
  }

  /* ---------------------------------------------------------------------- *
   * Saving
   * ---------------------------------------------------------------------- */

  function getCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  /** Zero every cart line carrying the given bundle id, addressed by fresh keys. */
  async function zeroBundle(bundleId) {
    var cart = await getCart();
    var updates = {};
    cart.items.forEach(function (item) {
      var bid = item.properties && item.properties._bundle_id;
      if (bid === bundleId) updates[item.key] = 0;
    });
    if (Object.keys(updates).length === 0) return cart;
    var res = await postJson('/cart/update.js', { updates: updates });
    return res.ok ? res.json() : cart;
  }

  /** The line payload for the new bundle, plus titles for error messages. */
  function buildPayload(builder, totals, bundleId) {
    var consolidated = {};
    var order = [];
    builder.state.selectedItems.forEach(function (item) {
      var key = String(item.variantId);
      if (consolidated[key]) {
        consolidated[key].quantity += item.quantity;
      } else {
        consolidated[key] = { item: item, quantity: item.quantity };
        order.push(key);
      }
    });

    var items = [];
    var titles = {};
    var position = 1;

    order.forEach(function (key) {
      var entry = consolidated[key];
      var item = entry.item;
      titles[key] = item.title;
      items.push({
        id: parseInt(item.variantId, 10),
        quantity: entry.quantity,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_position': String(position),
          '_bundle_total_items': String(totals.itemCount),
          '_bundle_discount_tier': String(Math.round(totals.discountRate * 100)),
          '_bundle_original_price': String(item.price),
          '_bundle_discounted_price': String(item.price - Math.floor(item.price * totals.discountRate)),
          '_bundle_purchase_type': builder.state.purchaseType
        }
      });
      position++;
    });

    return { items: items, titles: titles };
  }

  /**
   * Compare what came back against what we asked for.
   *
   * This is correctness, not error handling. Shopify documents a 422 for only
   * two stock cases, fully sold out and all remaining stock already in the
   * cart. The commoner shortfall, asking for more than remains, is documented
   * as adding the maximum available quantity and returning success. Trusting
   * response.ok would let a 6-item bundle silently become 5, which the discount
   * function pays nothing for because it matches tier counts exactly.
   *
   * A new bundle id means no line can merge with an existing one, so returned
   * quantity must equal requested quantity exactly.
   */
  function findShortfalls(requested, response, titles) {
    var got = {};
    ((response && response.items) || []).forEach(function (line) {
      var vid = String(line.variant_id != null ? line.variant_id : line.id);
      got[vid] = (got[vid] || 0) + line.quantity;
    });

    var short = [];
    requested.forEach(function (r) {
      var vid = String(r.id);
      var have = got[vid] || 0;
      if (have < r.quantity) {
        short.push({ variantId: vid, title: titles[vid] || 'an item', wanted: r.quantity, got: have });
      }
    });
    return short;
  }

  function shortfallMessage(short) {
    var first = short[0];
    if (first.got === 0) {
      return 'We could not add ' + first.title + '. Your bundle is unchanged.';
    }
    return 'Only ' + first.got + ' of ' + first.title + ' ' +
      (first.got === 1 ? 'is' : 'are') + ' available, and your bundle needs ' +
      first.wanted + '. Your bundle is unchanged.';
  }

  /**
   * Replace the cart bundle with the current selection.
   *
   * Add first, then remove. The add is the step that can be refused, so doing
   * it first means a refusal leaves the customer's existing bundle untouched,
   * and a crash midway leaves a visible duplicate rather than an empty cart.
   * The removal cannot fail on stock: update.js is documented not to validate
   * quantity on variants already in the cart.
   *
   * The new bundle needs its own id for two reasons. The discount function sums
   * quantities per bundle id, so reusing the id would briefly merge both sets
   * into one group matching no tier at all, pricing the whole thing at zero.
   * And add.js merges lines whose variant and properties are byte identical, so
   * unchanged lines would merge while retiered ones would not, which would make
   * the quantity check above impossible.
   */
  async function save(builder) {
    if (builder.state.isAddingToCart) return;

    var totals = builder.calculateTotals();
    if (!totals.isValid || !isChanged(builder)) return;

    var soldOut = builder.state.selectedItems.filter(function (i) { return i.available === false; });
    if (soldOut.length > 0) {
      builder.showToast('Swap out ' + joinNames(soldOut.map(function (i) { return i.title; })) +
        ' before saving.');
      return;
    }

    builder.state.isAddingToCart = true;
    if (builder.elements.addToCartBtn) builder.elements.addToCartBtn.classList.add('is-loading');
    builder.render();

    var newBundleId = builder.generateBundleId();
    var payload = buildPayload(builder, totals, newBundleId);

    try {
      var addRes = await postJson('/cart/add.js', { items: payload.items });
      if (!addRes.ok) {
        var err = await addRes.json().catch(function () { return null; });
        throw new Error(
          (err && (err.description || err.message)) ||
          'We could not update your bundle. Your cart is unchanged.'
        );
      }

      var added = await addRes.json();
      var short = findShortfalls(payload.items, added, payload.titles);

      if (short.length > 0) {
        // Undo our own partial add and leave the original bundle exactly as it was.
        await zeroBundle(newBundleId);

        // The shortfall is sometimes caused by the customer's own bundle: their
        // existing lines and the new ones both count against available stock
        // while the two overlap. Only in that case does clearing the old bundle
        // first actually free anything, so only in that case is it offered. If
        // the stock simply is not there, tearing down their bundle would destroy
        // it for nothing.
        if (recoverableByFreeingStock(builder, short)) {
          renderRetryOffer(builder, short);
          builder.state.isAddingToCart = false;
          if (builder.elements.addToCartBtn) {
            builder.elements.addToCartBtn.classList.remove('is-loading');
          }
          builder.render();
          return;
        }

        throw new Error(shortfallMessage(short));
      }

      // The old bundle goes by property against a fresh read, never by a key
      // remembered from page load. Adding the new bundle changes the cart
      // subtotal, which changes how any cart-level discount allocates across
      // lines, which Shopify documents as changing the line key. A stale key is
      // a silent no-op that would leave two bundles in the cart.
      var cart = await getCart();
      var updates = {};
      cart.items.forEach(function (item) {
        var bid = item.properties && item.properties._bundle_id;
        if (bid && bid !== newBundleId) updates[item.key] = 0;
      });

      if (Object.keys(updates).length > 0) {
        var upRes = await postJson('/cart/update.js', { updates: updates });
        if (upRes.ok) cart = await upRes.json();
      }

      // One bundle is an invariant of the write, not an assumption of the UI.
      var remaining = distinctBundleIds(cart);
      if (remaining.length > 1) {
        var retry = {};
        cart.items.forEach(function (item) {
          var bid = item.properties && item.properties._bundle_id;
          if (bid && bid !== newBundleId) retry[item.key] = 0;
        });
        if (Object.keys(retry).length > 0) {
          var retryRes = await postJson('/cart/update.js', { updates: retry });
          if (retryRes.ok) cart = await retryRes.json();
        }
      }

      builder.track('bundle_edit_saved', {
        item_count: totals.itemCount,
        value: totals.total / 100,
        bundle_savings: totals.totalDiscount / 100,
        discount_percent: Math.round(totals.discountRate * 100),
        currency: builder.config.currency || 'USD'
      });

      // Emitted only after the removal. Between the two writes the cart briefly
      // holds both bundles, and the drawer renders one block per bundle id.
      builder.editSaved = true;
      refreshCartDisplay();
      window.location.href = '/cart?bundle_updated=1';

    } catch (error) {
      builder.showToast(error.message || 'Something went wrong. Your bundle is unchanged.');
      builder.state.isAddingToCart = false;
      if (builder.elements.addToCartBtn) builder.elements.addToCartBtn.classList.remove('is-loading');
      builder.render();
    }
  }

  /**
   * Would clearing the old bundle actually free the stock we came up short on?
   * True only when a short variant is one the customer's existing bundle holds.
   */
  function recoverableByFreeingStock(builder, short) {
    var original = builder.editOriginalItems || [];
    return short.some(function (s) {
      return original.some(function (o) {
        return String(o.variantId) === String(s.variantId) && o.quantity > 0;
      });
    });
  }

  function itemsToLines(items, rate, bundleId, purchaseType) {
    var count = items.reduce(function (sum, i) { return sum + i.quantity; }, 0);
    var position = 1;
    return items.map(function (item) {
      return {
        id: parseInt(item.variantId, 10),
        quantity: item.quantity,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_position': String(position++),
          '_bundle_total_items': String(count),
          '_bundle_discount_tier': String(Math.round(rate * 100)),
          '_bundle_original_price': String(item.price),
          '_bundle_discounted_price': String(item.price - Math.floor(item.price * rate)),
          '_bundle_purchase_type': purchaseType || 'one-time'
        }
      };
    });
  }

  /**
   * Offer the swap as a decision rather than taking it. Removing first is the
   * only order that can succeed here, but it also opens a window where the
   * customer has no bundle at all, so it is theirs to choose.
   */
  function renderRetryOffer(builder, short) {
    var host = builder.elements.addToCartBtn && builder.elements.addToCartBtn.parentElement;
    if (!host) return;

    var existing = host.querySelector('.bundle-sidebar__edit-retry');
    if (existing) existing.remove();

    var panel = document.createElement('div');
    panel.className = 'bundle-sidebar__edit-retry';
    panel.setAttribute('role', 'alert');
    panel.innerHTML =
      '<p class="bundle-sidebar__edit-retry-text">' +
      escapeHtml(shortfallMessage(short)) +
      ' Your current bundle is holding some of that stock. We can clear it first, ' +
      'then save your new one.</p>' +
      '<button type="button" class="bundle-sidebar__edit-retry-btn">Clear it and save</button>';

    host.insertBefore(panel, host.firstChild);

    panel.querySelector('.bundle-sidebar__edit-retry-btn').addEventListener('click', function () {
      panel.remove();
      retryFreeingStock(builder);
    });
  }

  /**
   * Remove first, then add. The reverse of the normal order, used only when the
   * customer has chosen it. If the add still fails, put their original bundle
   * back from the snapshot taken at hydration.
   */
  async function retryFreeingStock(builder) {
    if (builder.state.isAddingToCart) return;

    var totals = builder.calculateTotals();
    builder.state.isAddingToCart = true;
    if (builder.elements.addToCartBtn) builder.elements.addToCartBtn.classList.add('is-loading');
    builder.render();

    var newBundleId = builder.generateBundleId();
    var payload = buildPayload(builder, totals, newBundleId);

    try {
      var cart = await getCart();
      var updates = {};
      cart.items.forEach(function (item) {
        if (item.properties && item.properties._bundle_id) updates[item.key] = 0;
      });
      if (Object.keys(updates).length > 0) {
        await postJson('/cart/update.js', { updates: updates });
      }

      var addRes = await postJson('/cart/add.js', { items: payload.items });
      var added = addRes.ok ? await addRes.json() : null;
      var short = added ? findShortfalls(payload.items, added, payload.titles) : [{ title: 'an item', wanted: 0, got: 0 }];

      if (!added || short.length > 0) {
        if (added) await zeroBundle(newBundleId);
        await restoreOriginal(builder);
        throw new Error(
          'There was not enough stock after all, so we put your original bundle back.'
        );
      }

      builder.editSaved = true;
      builder.track('bundle_edit_saved', {
        item_count: totals.itemCount,
        value: totals.total / 100,
        discount_percent: Math.round(totals.discountRate * 100),
        recovered: true
      });
      refreshCartDisplay();
      window.location.href = '/cart?bundle_updated=1';

    } catch (error) {
      builder.showToast(error.message || 'We could not save your changes.');
      builder.state.isAddingToCart = false;
      if (builder.elements.addToCartBtn) builder.elements.addToCartBtn.classList.remove('is-loading');
      builder.render();
    }
  }

  /** Put back exactly what was in the cart when this page loaded. */
  async function restoreOriginal(builder) {
    var items = builder.editOriginalItems || [];
    if (items.length === 0) return;
    var lines = itemsToLines(items, builder.editOriginalRate || 0,
      builder.generateBundleId(), builder.state.purchaseType);
    try {
      await postJson('/cart/add.js', { items: lines });
    } catch (e) {
      // Nothing further we can do from here. The customer still has the cart
      // page and the builder, both of which will reflect reality on reload.
    }
  }

  /** Remove the bundle the customer came here to edit, then show them the cart. */
  async function removeBundle(builder) {
    if (builder.state.isAddingToCart) return;
    builder.state.isAddingToCart = true;
    builder.render();

    try {
      var cart = await getCart();
      var updates = {};
      cart.items.forEach(function (item) {
        if (item.properties && item.properties._bundle_id) updates[item.key] = 0;
      });

      if (Object.keys(updates).length > 0) {
        var res = await postJson('/cart/update.js', { updates: updates });
        if (res.ok) cart = await res.json();
      }

      builder.editSaved = true;
      builder.track('bundle_edit_removed', {});
      refreshCartDisplay();
      window.location.href = '/cart';

    } catch (error) {
      builder.showToast('We could not remove your bundle. Please try from the cart.');
      builder.state.isAddingToCart = false;
      builder.render();
    }
  }

  function distinctBundleIds(cart) {
    var seen = [];
    ((cart && cart.items) || []).forEach(function (item) {
      var bid = item.properties && item.properties._bundle_id;
      if (bid && seen.indexOf(bid) === -1) seen.push(bid);
    });
    return seen;
  }

  /**
   * Tell the theme its cart display is stale, without asserting a payload shape.
   *
   * Do NOT emit pubSubEvents.cartUpdate with a plain cart object. The theme's
   * subscriber in assets/cart.js reads `event.cart.sections[sectionName]`, and
   * that key only exists when the cart was fetched with sections requested,
   * which its own callers do and /cart/update.js does not. Passing a plain cart
   * throws inside the subscriber with "Cannot read properties of undefined",
   * and because emit is synchronous the exception takes our redirect with it,
   * so a save that actually succeeded looks like a failure.
   *
   * cart:refresh asks the theme to go and re-read the sections itself, which it
   * already does inside a try/catch, so it cannot take us down.
   */
  function refreshCartDisplay() {
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (e) {
      // A stale drawer is not worth blocking navigation for.
    }
  }

  function joinNames(names) {
    if (names.length === 1) return names[0];
    if (names.length === 2) return names[0] + ' and ' + names[1];
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text == null ? '' : String(text);
    return div.innerHTML;
  }

  return {
    readContext: readContext,
    hydrate: hydrate,
    renderBanner: renderBanner,
    lockReason: lockReason,
    lockLabel: lockLabel,
    signature: signature,
    isChanged: isChanged,
    applyButtonState: applyButtonState,
    save: save,
    removeBundle: removeBundle,
    distinctBundleIds: distinctBundleIds,
    trackSession: trackSession
  };
})();
